import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { recommendationsApi, Recommendation, PatientFormData } from '../../services/recommendations';

export const RecommendationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [patientData, setPatientData] = useState<PatientFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [patientDataLoading, setPatientDataLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'vaccines' | 'medications' | 'thought-process' | 'patient-data' | 'prompts'>('overview');

  useEffect(() => {
    const fetchRecommendation = async () => {
      if (!id) return;

      try {
        const data = await recommendationsApi.getRecommendation(id);
        setRecommendation(data);
      } catch (error) {
        console.error('Failed to fetch recommendation:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendation();
  }, [id]);

  // Fetch patient data when tab is selected
  useEffect(() => {
    const fetchPatientData = async () => {
      if (activeTab !== 'patient-data' || !id || patientData) return;

      setPatientDataLoading(true);
      try {
        const data = await recommendationsApi.getRecommendationFormData(id);
        setPatientData(data);
      } catch (error) {
        console.error('Failed to fetch patient data:', error);
      } finally {
        setPatientDataLoading(false);
      }
    };

    fetchPatientData();
  }, [activeTab, id, patientData]);

  if (loading) {
    return <div className="text-center py-8">Loading recommendation details...</div>;
  }

  if (!recommendation) {
    return <div className="text-center py-8">Recommendation not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold mb-4">{recommendation.traveler_name}</h1>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-600">Consultation</div>
            <div className="font-medium">{recommendation.consultation_number}</div>
          </div>
          <div>
            <div className="text-gray-600">Risk Level</div>
            <div className="font-medium">{recommendation.overall_review_level}</div>
          </div>
          <div>
            <div className="text-gray-600">Created</div>
            <div className="font-medium">
              {new Date(recommendation.created_at).toLocaleDateString()}
            </div>
          </div>
          <div>
            <div className="text-gray-600">Processing</div>
            <div className="font-medium">{recommendation.latency_ms}ms</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'vaccines', label: 'Vaccines' },
            { id: 'medications', label: 'Medications' },
            { id: 'thought-process', label: 'AI Analysis' },
            { id: 'patient-data', label: 'Patient Data' },
            { id: 'prompts', label: 'LLM Prompts' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-lg p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium mb-3">Recommendation Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded">
                  <div className="text-blue-900 font-medium">Vaccines</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {recommendation.final_recommendations?.vaccines?.length || 0}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded">
                  <div className="text-green-900 font-medium">Medications</div>
                  <div className="text-2xl font-bold text-green-600">
                    {recommendation.final_recommendations?.medications?.length || 0}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded">
                  <div className="text-purple-900 font-medium">Tokens Used</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {(recommendation.input_tokens + recommendation.output_tokens).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'vaccines' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Vaccine Recommendations</h3>
            {recommendation.final_recommendations?.vaccines?.map((vaccine, index) => (
              <div key={index} className="border-b pb-4 mb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-lg">{vaccine.vaccine_name}</h4>
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                    vaccine.status === 'RECOMMENDED' ? 'bg-green-100 text-green-800' :
                    vaccine.status === 'CONDITIONAL' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {vaccine.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Dose:</span> {vaccine.dose}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Route:</span> {vaccine.route}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Timing:</span> {vaccine.timing}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Therapy Class:</span> {vaccine.therapy_class}
                  </div>
                </div>
                
                <div className="mt-3">
                  <p className="text-gray-700"><span className="font-medium">Reasoning:</span> {vaccine.reasoning}</p>
                </div>
                
                {vaccine.series_schedule && (
                  <div className="mt-2">
                    <p className="text-gray-700"><span className="font-medium">Schedule:</span> {vaccine.series_schedule}</p>
                  </div>
                )}
                
                {vaccine.flags && vaccine.flags.length > 0 && (
                  <div className="mt-2">
                    <span className="font-medium text-gray-700">Flags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {vaccine.flags.map((flag, flagIndex) => (
                        <span key={flagIndex} className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {vaccine.counseling_points && vaccine.counseling_points.length > 0 && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">Counseling Points:</span>
                    <ul className="list-disc list-inside text-gray-600 text-sm mt-1">
                      {vaccine.counseling_points.map((point, pointIndex) => (
                        <li key={pointIndex}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )) || <div className="text-gray-500">No vaccines recommended</div>}
          </div>
        )}

        {activeTab === 'medications' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Medication Prescriptions</h3>
            {recommendation.final_recommendations?.medications?.map((med, index) => (
              <div key={index} className="border-b pb-4 mb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-lg">{med.medication_name}</h4>
                  <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                    med.status === 'RECOMMENDED' ? 'bg-green-100 text-green-800' :
                    med.status === 'CONDITIONAL' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {med.status}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Dose:</span> {med.dose}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Route:</span> {med.route}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Frequency:</span> {med.frequency}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Quantity:</span> {med.quantity}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Timing:</span> {med.timing}
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Therapy Class:</span> {med.therapy_class}
                  </div>
                </div>
                
                <div className="mt-3">
                  <p className="text-gray-700"><span className="font-medium">Reasoning:</span> {med.reasoning}</p>
                </div>
                
                {med.quantity_reasoning && (
                  <div className="mt-2">
                    <p className="text-gray-700"><span className="font-medium">Quantity Justification:</span> {med.quantity_reasoning}</p>
                  </div>
                )}
                
                {med.flags && med.flags.length > 0 && (
                  <div className="mt-2">
                    <span className="font-medium text-gray-700">Flags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {med.flags.map((flag, flagIndex) => (
                        <span key={flagIndex} className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                          {flag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {med.counseling_points && med.counseling_points.length > 0 && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-700">Counseling Points:</span>
                    <ul className="list-disc list-inside text-gray-600 text-sm mt-1">
                      {med.counseling_points.map((point, pointIndex) => (
                        <li key={pointIndex}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )) || <div className="text-gray-500">No medications prescribed</div>}
          </div>
        )}

        {activeTab === 'thought-process' && (
          <div>
            <h3 className="text-lg font-medium mb-4">AI Analysis & Reasoning</h3>
            <pre className="bg-gray-50 p-4 rounded text-sm overflow-auto">
              {JSON.stringify(recommendation.thought_process, null, 2)}
            </pre>
          </div>
        )}

        {activeTab === 'patient-data' && (
          <div>
            <h3 className="text-lg font-medium mb-4">Patient Form Data</h3>
            {patientDataLoading ? (
              <div className="text-center py-8 text-gray-500">Loading patient data...</div>
            ) : patientData ? (
              <div className="space-y-6">
                {/* Traveler Info */}
                <div className="bg-gray-50 p-4 rounded">
                  <h4 className="font-medium text-gray-900 mb-3">Traveler Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-medium">{patientData.traveler.name}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-medium">{patientData.traveler.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Date of Birth:</span>
                      <span className="ml-2 font-medium">
                        {patientData.traveler.date_of_birth
                          ? new Date(patientData.traveler.date_of_birth).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Relationship:</span>
                      <span className="ml-2 font-medium">{patientData.traveler.relationship}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Minor:</span>
                      <span className="ml-2 font-medium">{patientData.traveler.is_minor ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Form Status:</span>
                      <span className={`ml-2 font-medium ${
                        patientData.traveler.form_status === 'submitted' ? 'text-green-600' : 'text-yellow-600'
                      }`}>
                        {patientData.traveler.form_status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Travel Summary */}
                <div className="bg-blue-50 p-4 rounded">
                  <h4 className="font-medium text-blue-900 mb-3">Travel Summary</h4>
                  <div className="space-y-4">
                    {/* Destinations Detail */}
                    <div>
                      <span className="text-blue-700 font-medium">Destinations:</span>
                      <div className="mt-2 space-y-2">
                        {patientData.travel_summary.destinations.map((dest, i) => (
                          <div key={i} className="bg-blue-100 p-3 rounded">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-blue-900">
                                {dest.city ? `${dest.city}, ${dest.country}` : dest.country}
                              </span>
                              <span className="text-xs text-blue-700">
                                {dest.days} day{dest.days !== 1 ? 's' : ''} • {dest.region}
                              </span>
                            </div>
                            {dest.activities && dest.activities.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {dest.activities.map((activity, j) => (
                                  <span key={j} className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">
                                    {activity}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Trip Dates & Duration */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-blue-700">Departure:</span>
                        <span className="ml-2 font-medium text-blue-900">
                          {patientData.travel_summary.departure_date || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-blue-700">Return:</span>
                        <span className="ml-2 font-medium text-blue-900">
                          {patientData.travel_summary.return_date || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-blue-700">Duration:</span>
                        <span className="ml-2 font-medium text-blue-900">
                          {patientData.travel_summary.total_days} days
                        </span>
                      </div>
                      <div>
                        <span className="text-blue-700">Days Until Departure:</span>
                        <span className="ml-2 font-medium text-blue-900">
                          {patientData.travel_summary.days_until_departure ?? 'N/A'}
                        </span>
                      </div>
                    </div>

                    {/* Vaccination Timeline */}
                    {patientData.travel_summary.vaccination_timeline && (
                      <div>
                        <span className="text-blue-700 font-medium">Vaccination Timeline:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {patientData.travel_summary.vaccination_timeline.urgent && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                              URGENT
                            </span>
                          )}
                          {patientData.travel_summary.vaccination_timeline.standard && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                              Standard Timeline
                            </span>
                          )}
                          {patientData.travel_summary.vaccination_timeline.ideal && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                              Ideal Timeline
                            </span>
                          )}
                          {patientData.travel_summary.vaccination_timeline.days_available && (
                            <span className="text-xs text-blue-600">
                              ({patientData.travel_summary.vaccination_timeline.days_available} days available)
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Pharmacy */}
                {patientData.selected_pharmacy && (
                  <div className="bg-green-50 p-4 rounded">
                    <h4 className="font-medium text-green-900 mb-3">Selected Pharmacy</h4>
                    <div className="text-sm">
                      <div className="font-medium text-green-800">{patientData.selected_pharmacy.name}</div>
                      <div className="text-green-700">{patientData.selected_pharmacy.address}</div>
                      <div className="text-green-700">
                        {patientData.selected_pharmacy.phone} | {patientData.selected_pharmacy.email}
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Sections */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Form Sections</h4>
                  <div className="space-y-4">
                    {patientData.form_sections.map((section, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
                          <span className="font-medium">{section.display_name}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            section.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {section.completed ? 'Completed' : 'Incomplete'}
                          </span>
                        </div>
                        <div className="p-4 bg-white">
                          {Object.keys(section.data).length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                              {Object.entries(section.data).map(([key, value]) => (
                                <div key={key} className="border-b border-gray-100 pb-2">
                                  <span className="text-gray-500 text-xs block">
                                    {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </span>
                                  <span className="font-medium text-gray-900">
                                    {typeof value === 'boolean'
                                      ? (value ? 'Yes' : 'No')
                                      : Array.isArray(value)
                                        ? value.join(', ') || 'None'
                                        : typeof value === 'object'
                                          ? JSON.stringify(value)
                                          : String(value) || 'N/A'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-gray-500 text-sm">No data provided</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Failed to load patient data. Please try again.
              </div>
            )}
          </div>
        )}

        {activeTab === 'prompts' && (
          <div className="space-y-6">
            <h3 className="text-lg font-medium mb-4">LLM Prompts Used</h3>

            {/* System Prompt */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">System Prompt</h4>
                <span className="text-xs text-gray-500">
                  Sets the AI's role and behavior
                </span>
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-auto max-h-96 whitespace-pre-wrap">
                {recommendation.system_prompt || 'System prompt not available'}
              </pre>
            </div>

            {/* User Prompt */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">User Prompt</h4>
                <span className="text-xs text-gray-500">
                  Contains patient data, CDC info, and protocols
                </span>
              </div>
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-auto max-h-[600px] whitespace-pre-wrap">
                {recommendation.user_prompt || 'User prompt not available'}
              </pre>
            </div>

            {/* Token Usage Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Token Usage</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Input Tokens:</span>
                  <span className="ml-2 font-medium">{recommendation.input_tokens.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-600">Output Tokens:</span>
                  <span className="ml-2 font-medium">{recommendation.output_tokens.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Tokens:</span>
                  <span className="ml-2 font-medium">
                    {(recommendation.input_tokens + recommendation.output_tokens).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Model:</span>
                  <span className="ml-2 font-medium text-xs">{recommendation.llm_model_id}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};